import { inject, Injectable } from "@angular/core";

import { DeliveryRiskAssessmentReportRelatedRecordsService } from "@components/deliveries/services";
import { LocationRiskAssessmentReportRelatedRecordsService } from "@components/locations/services";
import { OrganisationRiskAssessmentReportRelatedRecordsService } from "@components/organisations/services";
import { ProductRiskAssessmentReportRelatedRecordsService } from "@components/products/services";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { AuthenticationService } from "@shared/services";
import { CommonUtils } from "@shared/utils";

import { RiskAssessmentReportsApiService } from "../api";
import { riskAssessmentReportResourceMap } from "../constants";
import { RiskAssessmentReportRecord } from "../models";

type GetParams = { id: string; type: RiskAssessmentTemplateResourceType };

type RecordService =
  | DeliveryRiskAssessmentReportRelatedRecordsService
  | LocationRiskAssessmentReportRelatedRecordsService
  | OrganisationRiskAssessmentReportRelatedRecordsService
  | ProductRiskAssessmentReportRelatedRecordsService;

const { COUNTRY, DELIVERY, LOCATION, MATERIAL, ORGANISATION, PRODUCT } =
  RiskAssessmentTemplateResourceType;

@Injectable({ providedIn: "root" })
export class RiskAssessmentReportRelatedRecordsService {
  private readonly authService = inject(AuthenticationService);

  private readonly reportsApi = inject(RiskAssessmentReportsApiService);

  private readonly deliveryRecordsService = inject(
    DeliveryRiskAssessmentReportRelatedRecordsService,
  );

  private readonly locationRecordsService = inject(
    LocationRiskAssessmentReportRelatedRecordsService,
  );

  private readonly organisationRecordsService = inject(
    OrganisationRiskAssessmentReportRelatedRecordsService,
  );

  private readonly productRecordsService = inject(ProductRiskAssessmentReportRelatedRecordsService);

  private readonly recordsServiceMap: Record<RiskAssessmentTemplateResourceType, RecordService> = {
    [COUNTRY]: undefined,
    [DELIVERY]: this.deliveryRecordsService,
    [LOCATION]: this.locationRecordsService,
    [MATERIAL]: undefined,
    [ORGANISATION]: this.organisationRecordsService,
    [PRODUCT]: this.productRecordsService,
  };

  async get({ id, type }: GetParams): Promise<RiskAssessmentReportRecord[]> {
    const service = this.recordsServiceMap[type];

    if (!service) {
      return [];
    }

    const records = (await service.get({ id })).filter(({ id }) => id);
    const reports = await this.getReports(records);

    return reports;
  }

  async getReports(
    records: Array<RiskAssessmentReportRecord["record"]>,
  ): Promise<RiskAssessmentReportRecord[]> {
    if (!records.length) {
      return [];
    }

    const baseUri = `/organisations/${this.authService.getActiveOrganisationId()}`;

    const uris = records.map(
      ({ id, type }) => `${baseUri}/${riskAssessmentReportResourceMap[type]}/${id}`,
    );

    const latestReports = await this.reportsApi.getLatest({ records: uris });

    const reports = [];

    for (const record of records) {
      const matchedReport = latestReports.find(
        ({ uri }) => CommonUtils.getUriId(uri) === record.id,
      );

      if (matchedReport) {
        reports.push({ ...matchedReport.report, record });
      }
    }

    return reports;
  }
}
